import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import type {Repository} from '@github-ui/current-repository'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {DesktopDownloadIcon} from '@primer/octicons-react'
import {Heading, Link, LinkButton, SegmentedControl, Stack, TextInput} from '@primer/react'
import {useState} from 'react'

import {GitCommandsBlock, GitCommandsProTip} from './GitCommandsBlock'
import styles from './QuickSetupBox.module.css'

export interface QuickSetupBoxProps {
  repo: Repository
  urls: {
    httpUrl: string
    sshUrl: string
    desktopUrl: string
  }
}

export function QuickSetupBox({repo, urls}: QuickSetupBoxProps) {
  const {name, ownerLogin, nameWithOwner = `${ownerLogin}/${name}`} = repo
  const branch = repo.defaultBranch || 'main'
  const {httpUrl, sshUrl, desktopUrl} = urls

  const [activeProtocol, setActiveProtocol] = useState<'http' | 'ssh'>('http')
  const currentUrl = activeProtocol === 'http' ? httpUrl : sshUrl
  const isMacOrWindows = useIsPlatform(['windows', 'mac'], true)

  return (
    <>
      <div className={styles.QuickSetup}>
        <Stack className={styles.Header} padding="spacious" gap="condensed">
          <Heading as="h3" variant="medium" className={styles.Title}>
            <strong>Quick setup</strong> — if you’ve done this kind of thing before
          </Heading>
          <Stack
            direction={{narrow: 'vertical', regular: 'horizontal'}}
            align={{narrow: 'stretch', regular: 'center'}}
            gap="normal"
          >
            {isMacOrWindows && (
              <>
                {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
                <LinkButton href={desktopUrl} size="small" leadingVisual={DesktopDownloadIcon}>
                  Set up in Desktop
                </LinkButton>
                <strong className={styles.OrSeparator}>or</strong>
              </>
            )}
            <Stack.Item grow>
              <Stack direction="horizontal" align="center" gap="condensed">
                <SegmentedControl aria-label="Clone protocol" size="small">
                  <SegmentedControl.Button
                    selected={activeProtocol === 'http'}
                    onClick={() => setActiveProtocol('http')}
                  >
                    HTTPS
                  </SegmentedControl.Button>
                  <SegmentedControl.Button selected={activeProtocol === 'ssh'} onClick={() => setActiveProtocol('ssh')}>
                    SSH
                  </SegmentedControl.Button>
                </SegmentedControl>
                <Stack.Item grow>
                  <TextInput
                    id="empty-setup-clone-url"
                    readOnly
                    spellCheck={false}
                    monospace
                    size="small"
                    value={currentUrl}
                    aria-label="Clone URL"
                    className={styles.CloneInput}
                    trailingAction={
                      <CopyToClipboardButton
                        size="small"
                        textToCopy={currentUrl}
                        ariaLabel="Copy URL to clipboard"
                        tooltipProps={{direction: 'nw'}}
                      />
                    }
                  />
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
          <p className={styles.Intro}>
            Get started by{' '}
            <Link inline href={`/${nameWithOwner}/new/${branch}`}>
              creating a new file
            </Link>{' '}
            or{' '}
            <Link inline href={`/${nameWithOwner}/upload/${branch}`}>
              uploading an existing file
            </Link>
            . We recommend every repository include a{' '}
            <Link inline href={`/${nameWithOwner}/new/${branch}?readme=1`}>
              README
            </Link>
            ,{' '}
            <Link inline href={`/${nameWithOwner}/new/${branch}?filename=LICENSE.md`}>
              LICENSE
            </Link>
            , and{' '}
            <Link inline href={`/${nameWithOwner}/new/${branch}?filename=.gitignore`}>
              .gitignore
            </Link>
            .
          </p>
        </Stack>
        <Stack padding="spacious" gap="normal">
          <GitCommandsBlock repoName={name} branch={branch} defaultUrl={httpUrl} />
        </Stack>
      </div>
      <GitCommandsProTip />
    </>
  )
}
